/**
 * Blog Creation Service
 *
 * Service layer for blog content management API.
 * Uses /content endpoints with content_id parameters.
 *
 * See also:
 * - ../hooks/useBlogCreationAPI.ts - React Query hooks
 * - ../components/BlogCreationCommandCenter/types.ts - Type definitions
 */

import { apiClient } from './apiClient';
import { useTenantStore } from '../stores/useTenantStore';
import {
  Content,
  ContentDetail,
  ContentListResponse,
  CreateContentData,
  ContentFilters,
  ContentStage,
  PublishResponse,
  PreviewPublishHtmlResponse,
  GenerationLogEntry,
  GenerationTimelineResponse,
  OutlineResponse,
  ApproveOutlineRequest,
  PostgenMetadataAsyncState,
} from '../components/BlogCreationCommandCenter/types';
import { generateHTMLFromRTE } from '../components/blog-templates';
import {
  confirmPublishWithAcknowledgement,
  buildGateItem,
  PublishCancelledError,
  type PublishConfirmationPayload,
  type SlugCollisionResult,
  type SlugConflict,
} from '../lib/prePublishGate';
import type { HumanQaState, HumanQaStatus } from '../lib/humanQa';
import type { HumanVerifyItemStatus, HumanVerifyState } from '../lib/humanVerify';

// Re-export types for consumers of this service
export type { PublishResponse, PreviewPublishHtmlResponse };
export { PublishCancelledError } from '../lib/prePublishGate';

export interface ScheduledPostInfo {
  content_id: string;
  keyword: string;
  scheduled_time: string;
  status: string;
}

export interface SkippedKeywordInfo {
  keyword: string;
  reason: string;
  content_id?: string | null;
  status?: string | null;
  created_at?: string | null;
  scheduled_publish_time?: string | null;
  retry_after_seconds?: number | null;
  action?: string | null;
}

export interface GenerateAndScheduleResponse {
  success: boolean;
  plan_name: string;
  scheduled_posts: ScheduledPostInfo[];
  skipped: SkippedKeywordInfo[];
  message: string;
}

export interface UpdateSlugResponse {
  old_url: string | null;
  new_url: string;
  slug: string;
  redirect_created: boolean;
}

/**
 * Response for GET /content/{id}/slug-availability — the editor's
 * live-typing check. Reuses the #537 slug-collision `SlugConflict` shape
 * so this and `checkSlugCollision` never disagree about what a conflict
 * looks like.
 */
export interface SlugAvailabilityResponse {
  normalized_slug: string;
  available: boolean;
  reason: 'invalid' | 'taken' | null;
  conflict: SlugConflict | null;
}

export interface WordPressStatusUpdateResponse {
  content_id: string;
  wordpress_status: 'draft' | 'publish' | string;
  synced: boolean;
  wordpress_post_id?: number | null;
  message: string;
}

// Judge Report types
export interface JudgeRuleCheck {
  name: string;
  status: 'pass' | 'warn' | 'fail';
  detail: string;
}

export interface JudgeScores {
  writing_quality?: number;
  seo_coherence?: number;
  content_value?: number;
  reader_experience?: number;
}

export interface JudgeFinding {
  issue: string;
  fix: string;
}

export interface JudgeReport {
  content_id: string;
  generated_at: string;
  rule_checks: JudgeRuleCheck[];
  scores: JudgeScores;
  overall: number | null;
  strengths: string[];
  findings: JudgeFinding[];
  verdict: string;
}

export type HumanQaReviewAction = 'approve' | 'reject' | 'assign';

export interface HumanQaReviewRequest {
  action: HumanQaReviewAction;
  reviewer?: string;
  notes?: string;
  assigned_to?: string;
}

export interface HumanQaReviewResponse {
  content_id: string;
  tenant_id: string;
  human_qa: HumanQaState;
}

export interface HumanQaQueueItem {
  id: string;
  tenant_id: string;
  keyword?: string | null;
  title?: string | null;
  reasons: string[];
  assigned_to?: string | null;
  status: HumanQaStatus;
  created_at?: string | null;
}

export interface HumanQaQueueResponse {
  items: HumanQaQueueItem[];
}

export interface HumanQaDetailResponse {
  human_qa: HumanQaState | null;
  human_verify?: HumanVerifyState | null;
  publish_confirmations: PublishConfirmationPayload[];
  postgen?: {
    metadata?: {
      applied?: boolean;
      fallback_fired?: boolean;
      per_field?: Record<string, unknown> | null;
    } | null;
    social?: {
      applied?: boolean;
      fallback_fired?: boolean;
    } | null;
    async?: PostgenMetadataAsyncState | null;
  } | null;
  meta_title?: string | null;
  meta_description?: string | null;
  title?: string | null;
  url_slug?: string | null;
  keyword?: string | null;
  status?: string | null;
  wordpress_post_id?: number | null;
  wordpress_status?: 'draft' | 'publish' | string | null;
  tenant_id?: string | null;
}

export interface HumanVerifyResolveRequest {
  item_id: string;
  status: Extract<HumanVerifyItemStatus, 'verified' | 'dismissed'>;
  note?: string;
  reviewer: string;
}

export interface HumanVerifyResolveResponse {
  content_id: string;
  tenant_id?: string | null;
  human_verify: HumanVerifyState;
}

/**
 * Count words in HTML content (strips tags and counts)
 */
function countWordsInHtml(html: string): number {
  // Remove HTML tags and decode entities
  const text = html
    .replace(/<[^>]+>/g, ' ')
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#039;/g, "'")
    .replace(/\s+/g, ' ')
    .trim();

  if (!text) return 0;

  // Split by whitespace and count non-empty words
  return text.split(/\s+/).filter(word => word.length > 0).length;
}

const API_BASE = '/blog-creation';

// Explicit-param-wins, then the active selection. No fallback to the AgileCo
// test tenant: a missing client selection is an explicit error at request
// time, not a silent switch to test data.
const getTenantId = (tenantId?: string | null): string => {
  const resolved = tenantId || useTenantStore.getState().currentTenant;
  if (!resolved) throw new Error('No tenant selected');
  return resolved;
};

/** A WordPress author option for the editor's author selector. */
export interface WpAuthor {
  id: number;
  name: string;
}

/**
 * Schema-preview endpoint response (GET /content/{id}/schema-preview).
 *
 * The endpoint is the SINGLE SOURCE OF TRUTH for what ships at publish. The
 * frontend never re-derives schema gating — it renders from this payload.
 *
 * - `jsonld`: the REAL composed JSON-LD graph nodes that would ship (per-node),
 *   honoring selection + gating + Yoast ownership + enrichment. Null on
 *   soft-fail or when no schemas would ship.
 * - `types`: per-type ship status with reasons. `willShip` comes from the real
 *   publisher gate (FAQ pairs present, is_howto, Yoast ownership, etc.).
 * - `fields`: per-field source provenance — each emitted field tagged
 *   `site_fact` | `authored` (postgen_schema → SHIPS) | `enrichment` |
 *   `tracked` (postgen_jsonld shadow, NOT shipped).
 * - `selection_active`: false when SCHEMA_SELECTION_PUBLISH_ENABLED is off →
 *   toggles are stored but the publisher currently ignores them (auto-default).
 * - `error`: null on success; a string on soft-fail (the drawer still opens
 *   with an honest "couldn't load" state).
 */
export interface SchemaPreviewTypeStatus {
  type: string;
  selected: boolean;
  willShip: boolean;
  reason: string | null;
}

export type SchemaFieldSource = 'site_fact' | 'authored' | 'enrichment' | 'tracked';

export interface SchemaFieldProvenance {
  schema_type: string;
  field: string;
  source: SchemaFieldSource;
  ships: boolean;
}

export interface SchemaPreviewResponse {
  jsonld: Array<Record<string, unknown>> | null;
  types: SchemaPreviewTypeStatus[];
  fields: SchemaFieldProvenance[];
  selection_active: boolean;
  error: string | null;
}

// ---------------------------------------------------------------------------
// Authored-schema types (LOCKED contract — sibling GLM sessions import these
// exact shapes against the drawer/service interface). The publisher reads these
// fields from generation_metadata.postgen_schema.fields, so edits MUST land
// there, not in a separate packet.
// ---------------------------------------------------------------------------

/** A single HowTo step. Text is editable; name is authored by the run. */
export interface HowToStepObject {
  name: string;
  text: string;
}

/** The HowTo object authored by the postgen_schema run (>=2 steps when present). */
export interface HowToObject {
  name: string;
  steps: HowToStepObject[];
}

/**
 * The full authored-schema fields packet. Mirrors the backend
 * ContentDetail.postgen_schema_fields typed extractor (Session A) and the
 * persisted fields under generation_metadata.postgen_schema.fields.
 */
export interface AuthoredSchemaFields {
  article_description: string;
  is_howto: boolean;
  howto: HowToObject | null;
  /**
   * Mechanical Home › Blog › <post> trail, always present when Family-E runs.
   * Optional/additive — absence falls back to the live schema-preview
   * derivation (byte-identical to today's behavior).
   */
  breadcrumbs?: BreadcrumbParticle[] | null;
  /**
   * Body-derived FAQ pairs, present when the article has FAQ blocks.
   * Optional/additive — absence falls back to the live schema-preview count.
   */
  faq?: AuthoredFaq | null;
}

/**
 * A single BreadcrumbList particle (position is 1-based: Home, Blog, <post>).
 * `url` is optional — the backend persists name+position only (publish always
 * re-derives the breadcrumb fresh at compose time, so a persisted url has no
 * consumer; the editor trail display is names-only).
 */
export interface BreadcrumbParticle {
  name: string;
  url?: string | null;
  position: number;
}

/** A single FAQ question/answer pair, body-derived. */
export interface FaqPair {
  question: string;
  answer: string;
}

/** The FAQ packet authored by the postgen_schema run — derived, not user-written. */
export interface AuthoredFaq {
  has_faq: boolean;
  pairs: FaqPair[];
}

/** Patch shape for updateAuthoredSchema — either or both fields may be present. */
export interface AuthoredSchemaPatch {
  article_description?: string;
  howto?: HowToObject | null;
}

export const blogCreationService = {
  // List all blog content with optional filters
  async list(
    filters?: ContentFilters,
    signal?: AbortSignal,
    tenantId?: string | null,
  ): Promise<ContentListResponse> {
    const params = new URLSearchParams();
    params.append('tenant_id', getTenantId(tenantId));
    if (filters?.status) params.append('status', filters.status);
    if (filters?.search) params.append('search', filters.search);
    if (filters?.page) params.append('offset', ((filters.page - 1) * (filters.page_size || 15)).toString());
    if (filters?.page_size) params.append('limit', filters.page_size.toString());
    if (filters?.include_deleted) params.append('include_deleted', 'true');

    const response = await apiClient.get<ContentListResponse>(
      `${API_BASE}/content/summary?${params.toString()}`,
      { signal }
    );
    return response.data;
  },

  // Pre-flight WP site health check (used before content creation)
  async checkWPHealth(): Promise<{ reachable: boolean; tenant_id: string; reason?: string }> {
    const tenantId = getTenantId();
    const response = await apiClient.get(`${API_BASE}/wp-health`, {
      params: { tenant_id: tenantId },
    });
    return response.data;
  },

  // Get single content with full details
  async get(contentId: string, tenantId?: string | null): Promise<ContentDetail> {
    const response = await apiClient.get<ContentDetail>(`${API_BASE}/content/${contentId}?tenant_id=${getTenantId(tenantId)}`);
    return response.data;
  },

  // WordPress authors for the tenant (editor author selector). Fail-soft: any
  // error resolves to an empty list so the UI falls back to the manual ID input.
  async getWpAuthors(tenantId?: string | null): Promise<WpAuthor[]> {
    try {
      const response = await apiClient.get<{ authors?: WpAuthor[] }>(
        `${API_BASE}/wp-authors`,
        { params: { tenant_id: getTenantId(tenantId) } },
      );
      return response.data?.authors ?? [];
    } catch (err) {
      console.warn('[blogCreationService] getWpAuthors failed; falling back to empty list', err);
      return [];
    }
  },

  // Get generation activity log for a content item
  async getGenerationLog(contentId: string, signal?: AbortSignal): Promise<{
    generation_started_at: string | null;
    generation_log: GenerationLogEntry[];
    status: string;
    progress_percentage: number;
    current_stage: string | null;
    error_message: string | null;
  }> {
    const response = await apiClient.get(
      `${API_BASE}/content/${contentId}/generation-log?tenant_id=${getTenantId()}`,
      { signal }
    );
    return response.data;
  },

  // Expert-only: attempt-partitioned generation timeline with run metadata.
  // Backend gates by platform role (403 for non-experts).
  async getGenerationTimeline(contentId: string, signal?: AbortSignal): Promise<GenerationTimelineResponse> {
    const response = await apiClient.get<GenerationTimelineResponse>(
      `${API_BASE}/content/${contentId}/generation-timeline?tenant_id=${getTenantId()}`,
      { signal }
    );
    return response.data;
  },

  /**
   * Schema preview — the REAL composed JSON-LD graph that would ship for this
   * article, with per-type ship status and per-field source provenance. Read-only,
   * fail-soft (the endpoint returns error:null on success or error:string on
   * soft-fail; the service never throws — callers get a payload with `error` set).
   *
   * Single source of truth for schema gating: the drawer renders ship status /
   * reasons / field sources from THIS response, never re-derived on the frontend.
   */
  async getSchemaPreview(
    contentId: string,
    tenantId?: string | null,
  ): Promise<SchemaPreviewResponse> {
    const response = await apiClient.get<SchemaPreviewResponse>(
      `${API_BASE}/content/${contentId}/schema-preview?tenant_id=${getTenantId(tenantId)}`,
    );
    return response.data;
  },

  // Create new blog content
  async create(data: CreateContentData): Promise<Content> {
    const response = await apiClient.post<Content>(`${API_BASE}/content?tenant_id=${getTenantId()}`, data, {
      timeout: 60_000, // 60s — content creation can be slow on cold DB pool starts
    });
    return response.data;
  },

  // Quick generate — preview which keyword will be picked (no content created)
  async getQuickGeneratePreview(): Promise<Array<{
    cluster_id: string;
    keyword: string;
    map_name: string;
    opportunity_score: number;
    secondary_keywords: string[];
  }>> {
    const response = await apiClient.get(
      `${API_BASE}/content/quick-generate/preview?tenant_id=${getTenantId()}`,
      { timeout: 15_000 }
    );
    return response.data;
  },

  // Quick generate — picks the best keyword from the active content plan and starts generation
  async quickGenerate(wordpressPublishMode: 'none' | 'draft' | 'publish' = 'none', clusterId?: string, exportToGdocs?: boolean, generationMode?: 'suggestion' | 'supervised', modelOverride?: string): Promise<{
    id: string;
    keyword: string;
    map_name: string;
    opportunity_score: number;
    status: string;
  }> {
    const response = await apiClient.post(
      `${API_BASE}/content/quick-generate?tenant_id=${getTenantId()}`,
      {
        publish_immediately: wordpressPublishMode === 'publish',
        wordpress_publish_mode: wordpressPublishMode,
        ...(clusterId ? { cluster_id: clusterId } : {}),
        ...(exportToGdocs ? { export_to_gdocs: true } : {}),
        ...(generationMode ? { generation_mode: generationMode } : {}),
        ...(modelOverride ? { model_override: modelOverride } : {}),
      },
      { timeout: 30_000 }
    );
    return response.data;
  },

  // Start pending content (trigger the pipeline)
  async start(contentId: string): Promise<Content> {
    const response = await apiClient.post<Content>(`${API_BASE}/content/${contentId}/start?tenant_id=${getTenantId()}`);
    return response.data;
  },

  // CR-3: Get outline for review
  async getOutline(contentId: string): Promise<OutlineResponse> {
    const response = await apiClient.get<OutlineResponse>(
      `${API_BASE}/content/${contentId}/outline?tenant_id=${getTenantId()}`
    );
    return response.data;
  },

  // CR-3: Approve outline and resume generation
  async approveOutline(contentId: string, edits?: ApproveOutlineRequest): Promise<Content> {
    const response = await apiClient.post<Content>(
      `${API_BASE}/content/${contentId}/approve-outline?tenant_id=${getTenantId()}`,
      edits || {},
    );
    return response.data;
  },

  /**
   * Publish completed content to WordPress.
   *
   * @param contentId - The content ID to publish
   * @param htmlContent - Optional HTML content to publish. When provided, the backend
   *   will use this HTML directly instead of generating it from content_json.
   *   This enables frontend-generated HTML to be the authoritative source.
   * @param publishImmediately - Optional flag to publish immediately (true) or save as draft (false)
   * @returns PublishResponse with WordPress post details
   */
  /**
   * Refresh the article's WordPress DRAFT from the saved content_json so
   * Preview shows what the editor holds (mirrors WP's own auto-save-then-
   * preview). The caller must persist editor changes first. Builder-aware:
   * for Elementor/UX-Builder tenants the backend regenerates the builder
   * payload — pushing raw HTML would be invisible there. Never publishes:
   * the backend 409s for live posts and for content with no WP post.
   */
  async previewSyncDraft(contentId: string, tenantId?: string | null): Promise<void> {
    await apiClient.post(
      `${API_BASE}/content/${contentId}/preview-sync?tenant_id=${getTenantId(tenantId)}`,
      {},
    );
  },

  async publish(contentId: string, htmlContent?: string, publishImmediately?: boolean, builderNativeOverride?: boolean, tenantId?: string | null): Promise<PublishResponse> {
    // Universal pre-publish metadata warning gate (warn-and-confirm, never
    // blocks). Ensure we have the ContentDetail metadata, evaluate, and abort
    // with PublishCancelledError if the user chooses "Go back". Fails open when
    // no dialog handler is registered (tests / pollers).
    const detail = await this.get(contentId, tenantId);
    const gateResult = await confirmPublishWithAcknowledgement([buildGateItem(detail, getTenantId(tenantId))]);
    if (!gateResult.proceed) throw new PublishCancelledError();

    const body: {
      html_content?: string;
      publish_immediately?: boolean;
      builder_native_override?: boolean;
      publish_confirmation?: PublishConfirmationPayload;
    } = {};
    if (htmlContent) {
      body.html_content = htmlContent;
    }
    if (publishImmediately !== undefined) {
      body.publish_immediately = publishImmediately;
    }
    if (builderNativeOverride !== undefined) {
      body.builder_native_override = builderNativeOverride;
    }
    if (gateResult.confirmation) {
      body.publish_confirmation = gateResult.confirmation;
    }
    const response = await apiClient.post<PublishResponse>(
      `${API_BASE}/content/${contentId}/publish?tenant_id=${getTenantId(tenantId)}`,
      Object.keys(body).length > 0 ? body : undefined
    );
    return response.data;
  },

  /**
   * Auto-publish content to WordPress (backend-tracked state).
   *
   * This endpoint is used for backend-driven auto-publish flow where:
   * 1. Content was created with publish_immediately=true
   * 2. Backend tracks publish_status (pending -> publishing -> published/failed)
   * 3. Frontend generates HTML and calls this endpoint when publish_status='pending'
   *
   * Unlike the regular publish() method, this updates backend publish_status tracking.
   *
   * @param contentId - The content ID to auto-publish
   * @param htmlContent - HTML content generated by frontend via generateHTMLFromRTE()
   * @param publishImmediately - Optional flag to publish immediately (true) or save as draft (false). Defaults to true.
   * @returns PublishResponse with WordPress post details
   */
  async autoPublish(
    contentId: string,
    htmlContent: string,
    publishImmediately: boolean = true,
    tenantId?: string | null,
  ): Promise<PublishResponse> {
    const response = await apiClient.post<PublishResponse>(
      `${API_BASE}/content/${contentId}/auto-publish?tenant_id=${getTenantId(tenantId)}`,
      {
        html_content: htmlContent,
        publish_immediately: publishImmediately
      }
    );
    return response.data;
  },

  /**
   * Preview the HTML that will be published to WordPress.
   *
   * This method generates HTML locally using the frontend's authoritative
   * `generateHTMLFromRTE()` converter. This ensures what the user sees in
   * preview matches what will be published.
   *
   * @param contentId - The content ID to preview
   * @returns Object with generated HTML, source indicator, and word count
   *
   * @example
   * ```typescript
   * const { html, source, word_count } = await blogCreationService.previewPublishHtml(contentId);
   * console.log(`Generated ${word_count} words from ${source}`);
   * ```
   */
  async previewPublishHtml(contentId: string): Promise<PreviewPublishHtmlResponse> {
    // Resolve the tenant ONCE up front and carry that exact value through both
    // the local-render and backend-fallback paths. Reading getTenantId() again
    // after the async fetch could pick up a mid-operation workspace switch and
    // classify this content against another tenant's host.
    const tenantId = getTenantId();

    // Fetch the content to get content_json
    const content = await this.get(contentId, tenantId);

    // Generate HTML locally using frontend converter (authoritative source)
    if (content.content_json) {
      const html = generateHTMLFromRTE(content.content_json, tenantId);
      return {
        html,
        source: 'frontend',
        word_count: countWordsInHtml(html),
      };
    }

    // Fallback: If no content_json available, request from backend
    // This handles legacy content that only has content_html
    const response = await apiClient.post<{ html: string; word_count?: number }>(
      `${API_BASE}/content/${contentId}/generate-html?tenant_id=${tenantId}`
    );

    return {
      html: response.data.html,
      source: 'backend',
      word_count: response.data.word_count || countWordsInHtml(response.data.html),
    };
  },

  // Unpublish/revert published content to draft
  async unpublish(contentId: string): Promise<{ success: boolean }> {
    const response = await apiClient.post(`${API_BASE}/content/${contentId}/unpublish?tenant_id=${getTenantId()}`);
    return response.data;
  },

  // Regenerate a specific stage or the entire pipeline
  /**
   * Re-run a generation stage.
   *
   * `options` is additive and optional; existing positional callers are
   * unchanged. It exists because omitting these two fields is not neutral:
   * the backend falls back to the row's STORED writer and publish mode, so a
   * restart replays whatever the previous run happened to use and can push a
   * draft live. Callers that care — notably the WebMCP tools, which have no
   * human watching — pass both explicitly.
   */
  async regenerate(
    contentId: string,
    stage: ContentStage,
    options?: { modelOverride?: string; wordpressPublishMode?: 'none' | 'draft' | 'publish' },
  ): Promise<Content> {
    const body: {
      stage: ContentStage;
      model_override?: string;
      wordpress_publish_mode?: 'none' | 'draft' | 'publish';
    } = { stage };
    if (options?.modelOverride) body.model_override = options.modelOverride;
    if (options?.wordpressPublishMode) body.wordpress_publish_mode = options.wordpressPublishMode;

    const response = await apiClient.post<Content>(`${API_BASE}/content/${contentId}/regenerate?tenant_id=${getTenantId()}`, body);
    return response.data;
  },

  // Delete blog content
  async delete(contentId: string): Promise<{ success: boolean }> {
    const response = await apiClient.delete(`${API_BASE}/content/${contentId}?tenant_id=${getTenantId()}`);
    return response.data;
  },

  // Batch delete multiple content items
  async batchDelete(contentIds: string[]): Promise<{ deleted: number; failed: number }> {
    const response = await apiClient.post(`${API_BASE}/content/batch-delete?tenant_id=${getTenantId()}`, {
      content_ids: contentIds,
    });
    return response.data;
  },

  // Batch publish multiple content items to WordPress
  // Frontend generates HTML for each item before calling this
  async batchPublish(items: Array<{
    content_id: string;
    html_content: string;
    builder_native_override?: boolean;
  }>, options?: { tenantId?: string | null }): Promise<{
    published: number;
    failed: number;
    results: Array<{
      content_id: string;
      success: boolean;
      wordpress_post_id?: number;
      post_url?: string;
      error?: string;
    }>;
  }> {
    // Pre-publish metadata gate for the whole batch: evaluate every item and
    // show ONE combined dialog. Fetch each ContentDetail best-effort (failures
    // never block the batch). Fails open when no dialog handler is registered.
    const tenantId = getTenantId(options?.tenantId);
    const details = await Promise.all(
      items.map((item) => this.get(item.content_id, options?.tenantId).catch(() => null)),
    );
    const gateItems = details
      .filter((detail): detail is ContentDetail => detail !== null)
      .map((detail) => buildGateItem(detail, tenantId));
    if (gateItems.length > 0) {
      const gateResult = await confirmPublishWithAcknowledgement(gateItems);
      if (!gateResult.proceed) throw new PublishCancelledError();
      const response = await apiClient.post(
        `${API_BASE}/content/batch-publish?tenant_id=${tenantId}`,
        {
          items,
          ...(gateResult.confirmation ? { publish_confirmation: gateResult.confirmation } : {}),
        }
      );
      return response.data;
    }

    const response = await apiClient.post(
      `${API_BASE}/content/batch-publish?tenant_id=${tenantId}`,
      { items }
    );
    return response.data;
  },

  async reviewHumanQa(
    contentId: string,
    body: HumanQaReviewRequest,
    tenantId?: string | null,
  ): Promise<HumanQaReviewResponse> {
    const response = await apiClient.post<HumanQaReviewResponse>(
      `${API_BASE}/content/${contentId}/qa-review?tenant_id=${getTenantId(tenantId)}`,
      body,
    );
    return response.data;
  },

  async listHumanQaQueue(options?: {
    tenantId?: string | null;
    status?: HumanQaStatus | 'all';
  }): Promise<HumanQaQueueResponse> {
    const params = new URLSearchParams();
    params.set('tenant_id', getTenantId(options?.tenantId));
    if (options?.status) params.set('status', options.status);
    const response = await apiClient.get<HumanQaQueueResponse>(
      `${API_BASE}/content/qa-pending?${params.toString()}`,
    );
    return response.data;
  },

  async getHumanQaDetail(contentId: string, tenantId?: string | null): Promise<HumanQaDetailResponse> {
    const response = await apiClient.get<HumanQaDetailResponse>(
      `${API_BASE}/content/${contentId}/qa-detail?tenant_id=${getTenantId(tenantId)}`,
    );
    return response.data;
  },

  async resolveHumanVerifyItem(
    contentId: string,
    body: HumanVerifyResolveRequest,
    tenantId?: string | null,
  ): Promise<HumanVerifyResolveResponse> {
    const response = await apiClient.post<HumanVerifyResolveResponse>(
      `${API_BASE}/content/${contentId}/human-verify?tenant_id=${getTenantId(tenantId)}`,
      body,
    );
    return response.data;
  },

  // Get content progress/status (for polling)
  async getStatus(contentId: string): Promise<{
    status: string;
    progress_percentage: number;
    current_step?: string;
    message?: string;
  }> {
    const response = await apiClient.get(`${API_BASE}/content/${contentId}/status?tenant_id=${getTenantId()}`);
    return response.data;
  },

  // Update content brief (manual override)
  async updateBrief(
    contentId: string,
    brief: Partial<{
      title: string;
      meta_description: string;
      outline: Array<{ heading: string; content_instructions: string }>;
    }>
  ): Promise<ContentDetail> {
    const response = await apiClient.patch<ContentDetail>(
      `${API_BASE}/content/${contentId}/brief?tenant_id=${getTenantId()}`,
      brief
    );
    return response.data;
  },

  /**
   * Patch top-level content fields (title, SEO metadata, url_slug, …).
   *
   * Same PATCH /blog-creation/content/:id endpoint the editors' auto-save
   * uses (ContentEditorPage/UniversalEditorPage handleSave) — use this for
   * small field updates that shouldn't resend the whole document.
   */
  async patchContent(
    contentId: string,
    fields: Partial<{
      title: string;
      /** SEO title override; null/empty preserves the publisher's H1 fallback. */
      meta_title: string | null;
      meta_description: string;
      /** Post excerpt override; null/empty clears back to the derived excerpt. */
      excerpt: string | null;
      /** Open Graph overrides; null/empty preserves the publisher fallback. */
      og_title: string | null;
      og_description: string | null;
      /** Open Graph image URL override; null/empty falls back to the featured image. */
      og_image_url: string | null;
      /** Twitter card overrides; null/empty preserves the publisher fallback. */
      twitter_title: string | null;
      twitter_description: string | null;
      /** Advanced SEO overrides. Null means use the automatic publisher value. */
      canonical_url: string | null;
      robots_override: 'index' | 'noindex' | null;
      wordpress_author_id: number | null;
      schema_types_injected: string[];
      url_slug: string;
      keyword: string | null;
      content_json: unknown;
      content_html: string;
      total_word_count: number;
    }>,
    tenantId?: string | null
  ): Promise<ContentDetail> {
    const response = await apiClient.patch<ContentDetail>(
      `${API_BASE}/content/${contentId}?tenant_id=${getTenantId(tenantId)}`,
      fields
    );
    return response.data;
  },

  /**
   * Persist authored-schema field edits (article_description and/or HowTo).
   *
   * Same PATCH /blog-creation/content/:id endpoint as patchContent, but
   * scoped to the two authored-schema fields. The backend writes them INTO
   * generation_metadata.postgen_schema.fields with a sibling
   * `edited=true`/`edited_at` stamp (mirrors the postgen_social block) so a
   * later async Family-E re-run will NOT clobber a field the human edited
   * (edited-guard — Session A owns that path).
   *
   * The caller is responsible for invalidating ['schema-preview', contentId]
   * + the content detail query on success — see useInvalidateSchemaPreview +
   * the UniversalEditorRailMount debounced-save effect.
   */
  async updateAuthoredSchema(
    contentId: string,
    patch: AuthoredSchemaPatch,
    tenantId?: string | null
  ): Promise<ContentDetail> {
    const response = await apiClient.patch<ContentDetail>(
      `${API_BASE}/content/${contentId}?tenant_id=${getTenantId(tenantId)}`,
      patch
    );
    return response.data;
  },

  /**
   * Enqueue an on-demand postgen metadata run (Families A+B) for this article.
   *
   * Returns 202 with the job info; 409 means a run is already queued/running
   * (surfaced as `alreadyRunning`). The backend guards user edits — fields
   * changed since enqueue are never overwritten.
   */
  async regenerateMetadata(
    contentId: string,
    tenantId?: string | null
  ): Promise<{ accepted: boolean; alreadyRunning: boolean }> {
    try {
      await apiClient.post(
        `${API_BASE}/content/${contentId}/metadata/regenerate?tenant_id=${getTenantId(tenantId)}`
      );
      return { accepted: true, alreadyRunning: false };
    } catch (err) {
      const status = (err as { response?: { status?: number } })?.response?.status;
      if (status === 409) return { accepted: false, alreadyRunning: true };
      throw err;
    }
  },

  /**
   * Per-field metadata suggestion (read-only, fail-soft).
   *
   * Calls the synchronous suggest endpoint which reuses the owning postgen
   * prompt builder + re-ask loop for a single field. Returns the suggested
   * value or null when no suggestion is available. Never writes to the DB.
   *
   * ``params`` (optional): per-field request context threaded to the backend
   * as-is (e.g. field="image_alt" reads `{ image_src: <url> }` — WS-3 phase 2,
   * vision). Omitted entirely (not sent as an explicit `undefined`) when not
   * provided, so existing 3-arg call sites are unaffected.
   */
  async suggestField(
    contentId: string,
    field: string,
    tenantId?: string | null,
    params?: Record<string, unknown>
  ): Promise<string | null> {
    const response = await apiClient.post<{
      field: string;
      value: string | null;
      model?: string;
      attempts?: number;
      error?: string;
    }>(
      `${API_BASE}/content/${contentId}/suggest-field?tenant_id=${getTenantId(tenantId)}`,
      params ? { field, params } : { field }
    );
    return response.data.value ?? null;
  },

  /**
   * Force-HowTo suggest — calls the same per-field suggest endpoint with
   * field="howto" and force:true, returning the HowTo OBJECT via the
   * response's `value_object` field (Session A adds the richer response
   * shape). The drawer uses this for the PREVIEW→ACCEPT "Force HowTo" flow
   * when is_howto=false.
   *
   * Returns null on soft-fail (no value_object, error, or empty shape). The
   * component renders a quiet toast on null; the field is never touched.
   */
  async suggestHowTo(
    contentId: string,
    tenantId?: string | null
  ): Promise<HowToObject | null> {
    const response = await apiClient.post<{
      field: string;
      value: string | null;
      value_object?: HowToObject | null;
      model?: string;
      attempts?: number;
      error?: string;
    }>(
      `${API_BASE}/content/${contentId}/suggest-field?tenant_id=${getTenantId(tenantId)}`,
      { field: 'howto', force: true }
    );
    const obj = response.data.value_object;
    if (!obj || typeof obj !== 'object') return null;
    if (!Array.isArray(obj.steps) || obj.steps.length === 0) return null;
    return obj;
  },

  /**
   * Change the URL slug of published content (WS-E).
   *
   * The backend updates the live WordPress slug, persists the WP-returned
   * slug/link to blog_content, and creates a 301 redirect old → new URL.
   * For content that is NOT yet published, use patchContent({ url_slug })
   * instead — publish paths prefer content.url_slug when set.
   *
   * @returns old/new URL, the (possibly WP-deduped) slug, and whether a
   *   redirect was created (skipped for `?p=ID` query-form old URLs).
   */
  async updateSlug(
    contentId: string,
    urlSlug: string,
    tenantId?: string | null
  ): Promise<UpdateSlugResponse> {
    const response = await apiClient.post<UpdateSlugResponse>(
      `${API_BASE}/content/${contentId}/slug?tenant_id=${getTenantId(tenantId)}`,
      { url_slug: urlSlug }
    );
    return response.data;
  },

  /**
   * Pre-publish slug-collision check (read-only, warn-but-allow).
   *
   * Calls GET /blog-creation/slug-collision to see if the slug is already
   * owned by a different published page/article for this tenant. Used by the
   * pre-publish gate enrichment to show an overridable warning.
   *
   * Fail-soft: any network/server error resolves to { collides: false } so
   * the gate never blocks publish on a flaky check.
   */
  async checkSlugCollision(
    slug: string,
    excludeContentId?: string,
    tenantId?: string | null,
  ): Promise<SlugCollisionResult> {
    try {
      const params = new URLSearchParams();
      params.set('tenant_id', getTenantId(tenantId));
      params.set('slug', slug);
      if (excludeContentId) params.set('exclude_id', excludeContentId);
      const response = await apiClient.get(
        `${API_BASE}/slug-collision?${params.toString()}`,
      );
      return response.data;
    } catch {
      return { collides: false, conflicts: [] };
    }
  },

  /**
   * Live-typing slug-availability check for the editor (read-only, advisory).
   *
   * Calls GET /content/{id}/slug-availability — the same tenant-scoped
   * conflict sources as checkSlugCollision, scoped to one content row and
   * excluding it from both sources. This is a SOFT guardrail (see
   * slugAvailability.ts canAutosaveSlugChange): a `conflict`/`invalid` result
   * is surfaced inline but never blocks the metadata autosave.
   *
   * Unlike checkSlugCollision this does NOT catch its own errors — the rail
   * mount's debounced-check effect catches network/abort failures itself so
   * it can distinguish an aborted (superseded) request from a genuine error.
   */
  async checkSlugAvailability(
    contentId: string,
    slug: string,
    tenantId?: string | null,
    signal?: AbortSignal,
  ): Promise<SlugAvailabilityResponse> {
    const response = await apiClient.get<SlugAvailabilityResponse>(
      `${API_BASE}/content/${contentId}/slug-availability`,
      {
        params: {
          tenant_id: getTenantId(tenantId),
          slug,
        },
        signal,
      },
    );
    return response.data;
  },

  // Update content HTML (manual editing)
  async updateContent(contentId: string, contentHtml: string): Promise<ContentDetail> {
    const response = await apiClient.patch<ContentDetail>(
      `${API_BASE}/content/${contentId}/content?tenant_id=${getTenantId()}`,
      { content_html: contentHtml }
    );
    return response.data;
  },

  // Trigger validation only
  async validate(contentId: string): Promise<ContentDetail> {
    const response = await apiClient.post<ContentDetail>(
      `${API_BASE}/content/${contentId}/validate?tenant_id=${getTenantId()}`
    );
    return response.data;
  },

  // Request indexing for published content
  async requestIndexing(contentId: string): Promise<{
    success: boolean;
    content_id: string;
    url: string;
    google: { success: boolean; message?: string; error?: string };
    bing: { success: boolean; message?: string; error?: string };
  }> {
    const response = await apiClient.post(
      `${API_BASE}/content/${contentId}/request-indexing?tenant_id=${getTenantId()}`
    );
    return response.data;
  },

  /**
   * Schedule content for future publishing.
   *
   * Sets the scheduled_publish_time and changes publish_status to 'scheduled'.
   * When the scheduled time arrives, the backend APScheduler will automatically
   * transition the content to 'pending' for auto-publish processing.
   *
   * @param contentId - The content ID to schedule
   * @param scheduledTime - ISO 8601 datetime string for when to publish (e.g., "2025-01-15T10:00:00Z")
   * @returns Object with id, title, scheduled_publish_time, and publish_status
   */
  async schedule(contentId: string, scheduledTime: string): Promise<{
    id: string;
    title: string;
    scheduled_publish_time: string | null;
    publish_status: string;
  }> {
    const response = await apiClient.patch(
      `${API_BASE}/content/${contentId}/schedule?tenant_id=${getTenantId()}`,
      { scheduled_time: scheduledTime }
    );
    return response.data;
  },

  /**
   * Cancel scheduled publishing and return content to draft status.
   *
   * Clears the scheduled_publish_time and sets publish_status back to null.
   * Only works for content that is currently scheduled.
   *
   * @param contentId - The content ID to cancel scheduling for
   * @returns Object with id, title, and cancelled status
   */
  async cancelSchedule(contentId: string): Promise<{
    id: string;
    title: string;
    scheduled_publish_time: null;
    publish_status: null;
  }> {
    const response = await apiClient.delete(
      `${API_BASE}/content/${contentId}/schedule?tenant_id=${getTenantId()}`
    );
    return response.data;
  },

  /**
   * Batch schedule multiple content items for publishing across multiple days.
   * Posts are distributed evenly across the specified number of days.
   */
  async batchSchedule(
    contentIds: string[],
    numDays: number = 5,
    startDate?: string,
    timeOfDay: string = "10:00"
  ): Promise<{
    scheduled: number;
    failed: number;
    results: Array<{
      content_id: string;
      scheduled_time: string | null;
      success: boolean;
      error?: string;
    }>;
  }> {
    const response = await apiClient.post(
      `${API_BASE}/content/batch-schedule?tenant_id=${getTenantId()}`,
      {
        content_ids: contentIds,
        num_days: numDays,
        start_date: startDate,
        time_of_day: timeOfDay,
      }
    );
    return response.data;
  },

  /**
   * Get count of pending keywords from the active content plan.
   *
   * Returns the number of keywords that haven't been used for content generation yet.
   * Useful for showing users how many posts can still be generated from their plan.
   *
   * @returns Object with count of pending keywords and the plan name
   */
  async getPendingKeywordsCount(): Promise<{
    count: number;
    plan_name: string | null;
  }> {
    const response = await apiClient.get(
      `${API_BASE}/pending-keywords-count?tenant_id=${getTenantId()}`
    );
    return response.data;
  },

  /**
   * Generate content from pending keywords and schedule them for publishing.
   *
   * This is a convenience method that:
   * 1. Fetches pending keywords from the active content plan
   * 2. Generates content for each keyword
   * 3. Schedules the generated content across the specified time span
   *
   * @param numPosts - Number of posts to generate (default: 5)
   * @param daysSpan - Number of days to spread the posts across (default: 5)
   * @param postsPerWeek - Target posts per week for distribution (default: 3)
   * @param startTime - Time of day to publish posts in HH:MM format (default: "09:00")
   * @param templateId - Optional template ID. If not provided, backend auto-selects.
   * @returns Object with success status, plan name, and array of scheduled posts
   */
  async generateAndSchedule(
    numPosts: number = 5,
    daysSpan: number = 5,
    postsPerWeek: number = 3,
    startTime: string = "09:00",
    templateId?: string,
    modelOverride?: string,
    imageModelOverride?: string,
    autoPublish: boolean = false,
    clusterIds?: string[],
    generationMode?: string,
    exportToGdocs: boolean = false,
    wordpressPublishMode: 'none' | 'draft' | 'publish' = autoPublish ? 'publish' : 'none',
  ): Promise<GenerateAndScheduleResponse> {
    const response = await apiClient.post(
      `${API_BASE}/batch-schedule?tenant_id=${getTenantId()}`,
      {
        num_posts: numPosts,
        days_span: daysSpan,
        posts_per_week: postsPerWeek,
        start_time: startTime,
        template_id: templateId,
        model_override: modelOverride || null,
        image_model_override: imageModelOverride || null,
        auto_publish: autoPublish,
        wordpress_publish_mode: wordpressPublishMode,
        export_to_gdocs: exportToGdocs,
        cluster_ids: clusterIds?.length ? clusterIds : undefined,
        generation_mode: generationMode || 'suggestion',
      }
    );
    return response.data;
  },

  /**
   * Update the WordPress status of a content item.
   *
   * @param contentId - The content ID to update
   * @param wordpressStatus - The new WordPress status ('draft' or 'publish')
   * @returns Updated content item
   */
  async updateWordPressStatus(
    contentId: string,
    wordpressStatus: 'draft' | 'publish',
    options?: { tenantId?: string | null; syncImmediately?: boolean }
  ): Promise<WordPressStatusUpdateResponse> {
    const response = await apiClient.patch<WordPressStatusUpdateResponse>(
      `${API_BASE}/content/${contentId}/wordpress-status?tenant_id=${getTenantId(options?.tenantId)}`,
      {
        wordpress_status: wordpressStatus,
        ...(options?.syncImmediately !== undefined
          ? { sync_immediately: options.syncImmediately }
          : {}),
      }
    );
    return response.data;
  },

  // =============================================================================
  // Quality Report API
  // =============================================================================

  /**
   * Get quality report for a content item.
   *
   * Returns comprehensive quality assessment including:
   * - Reality Filter validation results
   * - Authority Signals count and details
   * - SERP Analysis word count comparison
   * - Model/Persona rotation info
   * - Overall score and recommendations
   *
   * @param contentId - The content ID to get report for
   * @returns Quality report with all validation phases
   */
  async getQualityReport(contentId: string): Promise<import('@/types/qualityReport').QualityReportResponse> {
    const response = await apiClient.get<import('@/types/qualityReport').QualityReportResponse>(
      `${API_BASE}/content/${contentId}/quality-report?tenant_id=${getTenantId()}`
    );
    return response.data;
  },

  /**
   * Run quality analysis on content.
   *
   * Triggers on-demand quality analysis:
   * - Runs Reality Filter to detect potential hallucinations
   * - Extracts Authority Signals from links in content
   * - Stores results in generation_metadata
   * - Returns comprehensive quality report
   *
   * Use this to analyze content generated before quality gates
   * were enabled, or to refresh analysis after edits.
   *
   * @param contentId - The content ID to analyze
   * @returns Quality report with analysis results
   */
  async runQualityAnalysis(contentId: string): Promise<import('@/types/qualityReport').QualityReportResponse> {
    const response = await apiClient.post<import('@/types/qualityReport').QualityReportResponse>(
      `${API_BASE}/content/${contentId}/quality-report/analyze?tenant_id=${getTenantId()}`
    );
    return response.data;
  },

  // =============================================================================
  // Judge Report API
  // =============================================================================

  async getJudgeReport(contentId: string, forceRefresh = false): Promise<JudgeReport> {
    const response = await apiClient.get<JudgeReport>(
      `${API_BASE}/content/${contentId}/judge-report`,
      { params: { tenant_id: getTenantId(), force_refresh: forceRefresh } },
    );
    return response.data;
  },

  async applyJudgeFixes(contentId: string): Promise<{ content_id: string; message: string; findings_count: number }> {
    const response = await apiClient.post(
      `${API_BASE}/content/${contentId}/apply-fixes`,
      null,
      { params: { tenant_id: getTenantId() } },
    );
    return response.data;
  },

  // =============================================================================
  // Bridge Publishing API
  // =============================================================================

  /** Check if bridge publishing is enabled for the current tenant. */
  async getBridgeStatus(): Promise<{ bridge_enabled: boolean }> {
    const response = await apiClient.get<{ bridge_enabled: boolean }>(
      `/bridge/status?tenant_id=${getTenantId()}`
    );
    return response.data;
  },

  /** Toggle bridge publishing for a specific content item. */
  async toggleBridge(contentId: string, useBridge: boolean): Promise<{ id: string; use_bridge: boolean }> {
    const response = await apiClient.patch<{ id: string; use_bridge: boolean }>(
      `/bridge/content/${contentId}?tenant_id=${getTenantId()}`,
      { use_bridge: useBridge }
    );
    return response.data;
  },

  async exportToGoogleDocs(
    contentId: string,
    tenantId: string
  ): Promise<{ gdocs_url: string; gdocs_doc_id: string; message: string }> {
    const { data } = await apiClient.post(
      `/google-docs/content/${contentId}/export`,
      {},
      {
        params: { tenant_id: tenantId },
        timeout: 120_000, // 2 min — export makes 3 Google API round-trips
      }
    );
    return data;
  },
};
